import ArrowUpIcon from '@/assets/svg/ui-elements/arrow-up.svg';
import RefreshIcon from '@/assets/svg/ui-elements/refresh.svg';
import { type WalletSendSuggestion } from '@/features/wallet-access/use-wallet-send-suggestion';

import { type SendAction, type SendActionsProps } from './send-actions';
import { SendFlowView } from './send-flow-view';
import { type SendFlowViewModel } from './use-send-flow';

export interface SendFlowActionContext {
  presentation: 'modal' | 'page';
  flow: SendFlowViewModel;
  suggestion: WalletSendSuggestion;
  walletBusy: boolean;
}

type ActionsForView = (context: SendFlowActionContext) => SendActionsProps;

const closeAction = (flow: SendFlowViewModel): SendAction => ({
  label: 'Close',
  onClick: flow.handleClose,
});

const reauthenticate = (flow: SendFlowViewModel) => () => {
  void flow.handleReauthenticate();
};

const failedTransferAction = (flow: SendFlowViewModel): SendAction => ({
  label: flow.requiresReauthentication ? 'Unlock wallet' : 'Try Again',
  icon: <RefreshIcon />,
  variant: 'outline',
  onClick: flow.requiresReauthentication ? reauthenticate(flow) : flow.handleRetry,
});

const SEND_FLOW_ACTIONS: Record<SendFlowView, ActionsForView> = {
  [SendFlowView.Verification]: () => ({}),
  [SendFlowView.Suggestion]: ({ suggestion }) => ({
    secondaryAction: { label: 'Add authenticator', onClick: suggestion.addAuthenticator },
    primaryAction: { label: 'Continue', onClick: suggestion.continueSend },
  }),
  [SendFlowView.Processing]: ({ presentation, flow }) =>
    presentation === 'page' ? {} : { secondaryAction: closeAction(flow) },
  [SendFlowView.Success]: ({ flow }) => ({ secondaryAction: closeAction(flow) }),
  [SendFlowView.Uncertain]: ({ flow }) => ({
    secondaryAction: closeAction(flow),
    primaryAction: { label: 'Check status', onClick: flow.handleRetry },
  }),
  [SendFlowView.Locked]: ({ flow, walletBusy }) => ({
    secondaryAction: closeAction(flow),
    primaryAction: { label: 'Unlock wallet', disabled: walletBusy, onClick: reauthenticate(flow) },
  }),
  [SendFlowView.Failed]: ({ flow }) => ({
    secondaryAction: closeAction(flow),
    primaryAction: failedTransferAction(flow),
  }),
  [SendFlowView.Confirmation]: ({ flow, suggestion }) => ({
    secondaryAction: { label: 'Back', onClick: flow.cancelConfirmation },
    primaryAction: {
      label: 'Confirm',
      icon: <ArrowUpIcon />,
      disabled: suggestion.checking,
      onClick: () => void suggestion.request(flow.handleSend),
    },
  }),
  [SendFlowView.Form]: ({ flow }) => ({
    primaryAction: {
      label: 'Send',
      icon: <ArrowUpIcon />,
      disabled: !flow.canContinue,
      onClick: flow.startConfirmation,
    },
  }),
};

export const sendFlowActions = (
  view: SendFlowView,
  context: SendFlowActionContext,
): SendActionsProps => SEND_FLOW_ACTIONS[view](context);
